import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * One-time exchange of session_id (Emergent OAuth fragment) for a session_token cookie.
 * Runs as soon as URL has `#session_id=...`. Uses a ref-guard for StrictMode safety.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : "";

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        await exchangeSession(sessionId);
        // Strip the hash, then send to dashboard
        window.history.replaceState({}, document.title, window.location.pathname);
        toast.success("Signed in");
        navigate("/", { replace: true });
      } catch (err) {
        const msg = err?.response?.data?.detail || "Sign-in failed. Please try again.";
        toast.error(msg);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, exchangeSession]);

  return (
    <div className="fixed inset-0 flex items-center justify-center text-zinc-500" data-testid="auth-callback">
      <Loader2 size={16} className="animate-spin mr-2" />
      <span className="text-sm">Finishing sign-in…</span>
    </div>
  );
}
